import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router } from "@angular/router";
import { RedesignVariantService } from "../services/redesign-variant.service";

/**
 * Maps a matched classic route to the redesigned sibling it should redirect to,
 * preserving whatever path/matrix params were on the original URL. Returns null
 * for anything this guard isn't attached to defensively (should not happen given
 * how it's wired in app.routes.ts, but a guard should never assume its own wiring).
 */
function redesignedCommands(route: ActivatedRouteSnapshot): any[] | null {
  const ownPath = route.routeConfig?.path;
  const parentPath = route.parent?.routeConfig?.path;
  const params = route.params;

  if (ownPath === "start") {
    return ["/start-redesign"];
  }
  if (parentPath === "login") {
    return params["checkoutId"] !== undefined
      ? ["/login-redesign", "checkout", params["checkoutId"]]
      : ["/login-redesign"];
  }
  if (ownPath === "cart") {
    // Only plain /cart: the takeout-N variants add an item first, then navigate to /cart themselves.
    return ["/cart-redesign"];
  }
  if (ownPath === "samples") {
    return ["/samples-redesign"];
  }
  if (ownPath === "review-box") {
    // The remote designer embeds the classic review page without the site chrome, so leave that one alone.
    return params["isRemoteAdmin"] === undefined ? ["/review-box-redesign", params] : null;
  }
  if (ownPath === "box-category") {
    return ["/box-category-redesign", { upload: params["upload"] }];
  }
  if (parentPath === "account") {
    // "" is the overview; the others mirror their classic sibling's own path.
    return ownPath ? ["/account-redesign", ownPath] : ["/account-redesign"];
  }
  if (parentPath === "upload-design") {
    // The category/subcategory path params only exist on the second route; everything else the
    // classic page received (container id, print sides, materials...) is matrix params, which
    // Angular merges into `params` - forward them to the redesigned route the same way.
    const { category, subcategory, ...matrix } = params;
    return category !== undefined
      ? ["/upload-design-redesign", category, subcategory, matrix]
      : ["/upload-design-redesign", matrix];
  }
  if (ownPath === "order-history") {
    return ["/order-history-redesign"];
  }
  if (ownPath === "continue-project") {
    return ["/online-designs-redesign"];
  }
  if (parentPath === "box-style") {
    return params["uploadDesign"] !== undefined
      ? ["/box-style-redesign", params["category"], params["uploadDesign"]]
      : ["/box-style-redesign", params["category"]];
  }
  if (parentPath === "box-options") {
    return params["uploadDesign"] !== undefined
      ? ["/box-options-redesign", params["category"], params["subcategory"], params["uploadDesign"]]
      : ["/box-options-redesign", params["category"], params["subcategory"]];
  }
  if (parentPath === "design-3d") {
    // Unlike the other mappings above, `params` here carries more than path params: box-options's
    // navigate() attaches the whole productDetails object (dimensions, print, material, etc.) as
    // matrix params on the last segment, and Angular merges those into this snapshot's params too.
    // Forwarding `params` itself as the trailing command re-attaches all of it to the redesigned
    // route, the same way box-options-redesign.component.ts's own goToBuilder() does.
    return ["/design-3d-redesign", params["category"], params["subcategory"], params];
  }
  return null;
}

/**
 * Attached to the classic start/box-category/box-style/box-options/design-3d,
 * login and account/order-history routes. A no-op for every visitor (returns true immediately) unless
 * RedesignVariantService says this session opted into the redesign - see that
 * service for how the choice is made (a `?design=redesign` link, never
 * randomized).
 */
export const redesignRedirectGuard: CanActivateFn = (route) => {
  const variant = inject(RedesignVariantService);

  if (!variant.isRedesign()) {
    return true;
  }

  const commands = redesignedCommands(route);
  if (!commands) {
    return true;
  }

  const router = inject(Router);
  return router.createUrlTree(commands);
};
